import type { Building } from "building-types/building/buildingTypes";
import type { HalfEdgeToFloorPath } from "building-types/floors/floorSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { makeFloorPaths } from "../elements/floor/floorSystem";
import getTargetedPorchSettings from "../elements/porch/getTargetedPorchSettings";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  settingsSets,
  optionChoices,
  doc: {
    settings: docSettings,
    entitySettings,
    frameFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
  },
  levels,
  planarGraphs,
  planarGraphLevelAssociations,
  foundationAssemblyOverrides,
  rooms,
  roomsByLevel,
  roomLevelIndices,
  roomFloorAssemblies,
  roomClassFloorAssemblyGroups,
  walls,
  wallMasses,
  interiorPalette,
  exteriorPalette,
}: Building) {
  return {
    levels,
    planarGraphs,
    planarGraphLevelAssociations,
    rooms,
    roomsByLevel,
    roomLevelIndices,
    roomFloorAssemblies,
    roomClassFloorAssemblyGroups,
    walls,
    wallMasses,
    frameFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings: settings.foundation,
    foundationAssemblyOverrides,
    doorSettings: settings.door,
    wallSettings: settings.wall,
    porchSettings: settings.porch,
    rearPorchSettings: settings.rearPorch ?? settings.porch,
    columnSettings: settings.column,
    interiorPalette,
    exteriorPalette,
    settingsSets,
    settings,
    optionChoices,
    docSettings,
    entitySettings,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  foundationSettings: "shallow",
  optionChoices: "shallow",
});

function generate(input: ReturnType<typeof select>): {
  floorPaths: Table<HalfEdgeToFloorPath>;
  issues: BuildingGenerationIssue[];
} {
  if (Object.values(input.levels).length === 0) {
    return { floorPaths: {}, issues: [] };
  }

  const issues: BuildingGenerationIssue[] = [];
  const floorPaths: RWTable<HalfEdgeToFloorPath> = {};
  const targetedPorchSettings = getTargetedPorchSettings(
    {
      settingsSets: input.settingsSets,
      optionChoices: input.optionChoices,
      settings: input.settings,
      rooms: input.rooms,
    },
    { settings: input.docSettings, entitySettings: input.entitySettings },
  );

  for (const levelId in input.levels) {
    const levelGraph = input.planarGraphs[levelId];
    const levelRooms = roomsOnLevel(levelId, input.rooms, input.roomsByLevel);

    const levelFloorPaths = makeFloorPaths({
      levelId,
      levels: input.levels,
      planarGraph: levelGraph,
      planarGraphLevelAssociations: input.planarGraphLevelAssociations[levelId],
      rooms: levelRooms,
      roomLevelIndices: input.roomLevelIndices,
      roomFloorAssemblies: input.roomFloorAssemblies,
      roomClassFloorAssemblyGroups: input.roomClassFloorAssemblyGroups,
      frameFloorAssemblies: input.frameFloorAssemblies,
      framingComponents: input.framingComponents,
      slabComponents: input.slabComponents,
      sheathingComponents: input.sheathingComponents,
      wallSystem: input.walls[levelId],
      interiorPalette: input.interiorPalette,
      exteriorPalette: input.exteriorPalette,
      settings: {
        wallSettings: input.wallSettings,
        porchSettings: input.porchSettings,
        rearPorchSettings: input.rearPorchSettings,
        columnSettings: input.columnSettings,
        foundationSettings: input.foundationSettings,
      },
      targetedPorchSettings,
    });

    if (!levelFloorPaths) {
      throw new Error(`Floor paths not found for level ${levelId}`);
    }

    floorPaths[levelId] = levelFloorPaths;
  }

  return { floorPaths, issues };
}

export default memo(select, generate, compare);
